import React, { useMemo } from "react";
import { generateRealTimePreview } from "../../utils/start-message-validation";

interface StartMessagePreviewProps {
  content: string;
  className?: string;
  conversationName?: string;
  userName?: string;
  showAnalytics?: boolean;
  showRecommendations?: boolean;
}

export function StartMessagePreview({
  content,
  className = "",
  conversationName = "Sample Conversation",
  userName = "Sample User",
  showAnalytics = true,
  showRecommendations = true,
}: StartMessagePreviewProps): React.ReactElement {
  // Generate real-time preview with analytics and recommendations
  const { preview, validation, recommendations } = useMemo(
    () =>
      generateRealTimePreview(content, {
        conversationName,
        userName,
        showAnalytics,
      }),
    [content, conversationName, userName, showAnalytics]
  );

  if (!content.trim()) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="text-center py-8 text-gray-500">
          <svg
            className="w-12 h-12 mx-auto mb-3 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p>Enter message content to see preview</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* SMS Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
            <svg
              className="w-4 h-4 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900">SMS Preview</h3>
          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
            Mobile Devices
          </span>
        </div>

        {/* Mock phone interface */}
        <div className="bg-gray-100 rounded-lg p-3">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex items-start space-x-2">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-gray-600">SMS</span>
              </div>
              <div className="flex-1">
                <div
                  className={`text-white rounded-lg px-3 py-2 text-sm max-w-xs break-words ${
                    preview.analytics.fitsInSMS ? "bg-blue-500" : "bg-red-500"
                  }`}
                  data-testid="sms-preview"
                >
                  {preview.smsFormat || "Enter message content..."}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {preview.analytics.fitsInSMS
                    ? "Prefilled in SMS app"
                    : "⚠️ May be truncated in SMS"}
                </p>
                {showAnalytics && (
                  <div className="text-xs text-gray-600 mt-1">
                    <span
                      className={
                        preview.analytics.fitsInSMS
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {preview.analytics.totalWithLink}/160 chars
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 text-xs text-gray-600">
          <p>• Opens native SMS app with message prefilled</p>
          <p>• User taps send to complete interaction</p>
          <p>• Tracking link included automatically</p>
          {!preview.analytics.fitsInSMS && (
            <p className="text-red-600 font-medium">
              • ⚠️ Message may be split into multiple SMS messages
            </p>
          )}
        </div>
      </div>

      {/* Chat Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center mr-2">
            <svg
              className="w-4 h-4 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900">Chat Preview</h3>
          <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
            Desktop/Tablet
          </span>
        </div>

        {/* Mock chat interface */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="space-y-3">
            {/* System message */}
            <div className="flex justify-center">
              <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                Chat started
              </span>
            </div>

            {/* Auto-sent message */}
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-white">A</span>
              </div>
              <div className="flex-1">
                <div
                  className="bg-white border rounded-lg px-3 py-2 text-sm max-w-xs break-words"
                  data-testid="chat-preview"
                >
                  {preview.chatFormat || "Enter message content..."}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Auto-sent as first message
                </p>
                {showAnalytics && (
                  <div className="text-xs text-gray-600 mt-1">
                    <span className="text-green-600">
                      {preview.analytics.contentLength} chars (tracking in
                      metadata)
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Input area */}
            <div className="border-t pt-2">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Type your response..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1 text-sm"
                  disabled
                />
                <button
                  className="bg-purple-600 text-white px-3 py-1 rounded-lg text-sm"
                  disabled
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 text-xs text-gray-600">
          <p>• Opens chat interface automatically</p>
          <p>• Message sent as first automated response</p>
          <p>• User can continue conversation immediately</p>
        </div>
      </div>

      {/* Analytics and Tracking Information */}
      {showAnalytics && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <div className="w-5 h-5 bg-gray-400 rounded flex items-center justify-center mr-2">
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-gray-700">
              Message Analytics
            </h4>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Content:</span>
                <span className="font-medium">
                  {preview.analytics.contentLength} chars
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tracking Data:</span>
                <span className="font-medium">
                  {preview.analytics.trackingDataLength} chars
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">With Tracking:</span>
                <span
                  className={`font-medium ${
                    preview.analytics.exceedsLimit
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {preview.analytics.totalWithData} chars
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Tracking Link:</span>
                <span className="font-medium">
                  {preview.analytics.trackingLinkLength} chars
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">SMS Total:</span>
                <span
                  className={`font-medium ${
                    preview.analytics.fitsInSMS
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {preview.analytics.totalWithLink} chars
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">SMS Compatible:</span>
                <span
                  className={`font-medium ${
                    preview.analytics.fitsInSMS
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {preview.analytics.fitsInSMS ? "✓ Yes" : "✗ No"}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-600 space-y-1">
              <p>
                <strong>Sample Tracking ID:</strong> {preview.trackingData}
              </p>
              <p>
                <strong>Tracking Link:</strong>
                <span className="break-all ml-1" data-testid="tracking-link">
                  {preview.trackingLink}
                </span>
              </p>
              <p>
                <strong>Format:</strong> {"{shortUserId}-{shortConversationId}"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {showRecommendations && recommendations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <div className="w-5 h-5 bg-blue-500 rounded flex items-center justify-center mr-2">
              <svg
                className="w-3 h-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-blue-800">
              Recommendations
            </h4>
          </div>
          <ul className="text-xs text-blue-700 space-y-1">
            {recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
